/**
 * Task Quick Add Component
 * Inline input for rapid task creation with smart syntax and autocomplete
 *
 * Supports:
 *   #Project Name  - Link to project (autocomplete with spaces)
 *   @username      - Assign to user (autocomplete)
 *   !priority      - Priority (!now, !tomorrow, !week, !next, !someday)
 *   ^date          - Due date (^tomorrow, ^friday, ^+3d)
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input, Tag, message, Tooltip, Typography, AutoComplete } from "antd";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";

const { Text } = Typography;

// API URL (proxied through Vite)
const API_URL = "/api";

export interface TaskQuickAddProps {
  defaultProjectId?: string;
  onTaskCreated?: () => void;
}

// Token colors for visual feedback
const TOKEN_COLORS: Record<string, string> = {
  project: "blue",
  user: "cyan",
  priority: "orange",
  date: "green",
};

interface ParsedTokens {
  task_name: string;
  project_name: string | null;
  project_id: string | null;
  assignee_name: string | null;
  assignee_id: string | null;
  priority: string | null;
  due_date: string | null;
  raw_tokens: Record<string, string>;
}

interface AutocompleteOption {
  value: string;
  label: React.ReactNode;
  id: string;
  type: "project" | "user";
}

export const TaskQuickAdd: React.FC<TaskQuickAddProps> = ({
  defaultProjectId,
  onTaskCreated,
}) => {
  const [inputText, setInputText] = useState("");
  const [parsedTokens, setParsedTokens] = useState<ParsedTokens | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState<
    AutocompleteOption[]
  >([]);
  const [activeToken, setActiveToken] = useState<{
    type: "project" | "user";
    start: number;
    query: string;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch projects for autocomplete
  const { data: projectsData } = useList({
    resource: "projects",
    pagination: { pageSize: 100 },
    filters: [
      {
        field: "conditions",
        operator: "eq",
        value: "projectstatus != 'archived' AND projectstatus != 'completed'",
      },
    ],
  });

  // Fetch users for autocomplete
  const { data: usersData } = useList({
    resource: "users",
    pagination: { pageSize: 50 },
  });

  const projects = projectsData?.data || [];
  const users = usersData?.data || [];

  // Detect active token being typed (# or @)
  const detectActiveToken = useCallback((text: string, cursorPos: number) => {
    // Look backwards from cursor to find # or @
    let tokenStart = -1;
    let tokenType: "project" | "user" | null = null;

    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = text[i];
      if (char === "#") {
        tokenStart = i;
        tokenType = "project";
        break;
      } else if (char === "@") {
        tokenStart = i;
        tokenType = "user";
        break;
      } else if (char === " " && i < cursorPos - 1) {
        // Allow spaces within the token, but stop at start of input or after special chars
        const prevChar = i > 0 ? text[i - 1] : "";
        if (prevChar === "!" || prevChar === "^" || prevChar === "") {
          break;
        }
      }
    }

    if (tokenStart >= 0 && tokenType) {
      const query = text.substring(tokenStart + 1, cursorPos).toLowerCase();
      return { type: tokenType, start: tokenStart, query };
    }
    return null;
  }, []);

  // Update autocomplete options based on active token
  useEffect(() => {
    if (!activeToken) {
      setAutocompleteOptions([]);
      return;
    }

    const { type, query } = activeToken;

    if (type === "project") {
      const filtered = projects
        .filter((p: any) => p.projectname?.toLowerCase().includes(query))
        .slice(0, 8)
        .map(
          (p: any): AutocompleteOption => ({
            value: p.projectname,
            label: (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>#{p.projectname}</span>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {p.projectstatus}
                </Text>
              </div>
            ),
            id: p.id,
            type: "project",
          })
        );
      setAutocompleteOptions(filtered);
    } else if (type === "user") {
      const filtered = users
        .filter((u: any) => {
          const name = `${u.first_name || ""} ${
            u.last_name || ""
          }`.toLowerCase();
          const username = (u.user_name || "").toLowerCase();
          return name.includes(query) || username.includes(query);
        })
        .slice(0, 8)
        .map(
          (u: any): AutocompleteOption => ({
            value: u.user_name || `${u.first_name} ${u.last_name}`,
            label: (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>@{u.user_name || `${u.first_name} ${u.last_name}`}</span>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {u.first_name} {u.last_name}
                </Text>
              </div>
            ),
            id: u.id,
            type: "user",
          })
        );
      setAutocompleteOptions(filtered);
    }
  }, [activeToken, projects, users]);

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputText(value);

    // Get cursor position (approximate - at end for now)
    const cursorPos = value.length;
    const token = detectActiveToken(value, cursorPos);
    setActiveToken(token);

    // Parse tokens for display
    parseTokensLocally(value);
  };

  // Local token parsing for immediate feedback
  const parseTokensLocally = (text: string) => {
    if (!text.trim()) {
      setParsedTokens(null);
      return;
    }

    const tokens: ParsedTokens = {
      task_name: text,
      project_name: null,
      project_id: null,
      assignee_name: null,
      assignee_id: null,
      priority: null,
      due_date: null,
      raw_tokens: {},
    };

    // Extract #project (with spaces, until next token or end)
    const projectMatch = text.match(/#([^@!^]+?)(?=\s*[@!^]|$)/);
    if (projectMatch) {
      tokens.project_name = projectMatch[1].trim();
      tokens.raw_tokens.project = projectMatch[0];
    }

    // Extract @user (with spaces, until next token or end)
    const userMatch = text.match(/@([^#!^]+?)(?=\s*[#!^]|$)/);
    if (userMatch) {
      tokens.assignee_name = userMatch[1].trim();
      tokens.raw_tokens.user = userMatch[0];
    }

    // Extract !priority
    const priorityMatch = text.match(/!(now|tomorrow|week|next|someday)/i);
    if (priorityMatch) {
      tokens.priority = priorityMatch[1].toLowerCase();
      tokens.raw_tokens.priority = priorityMatch[0];
    }

    // Extract ^date
    const dateMatch = text.match(/\^(\S+)/);
    if (dateMatch) {
      tokens.due_date = dateMatch[1];
      tokens.raw_tokens.date = dateMatch[0];
    }

    // Clean task name by removing all tokens
    let cleanName = text;
    Object.values(tokens.raw_tokens).forEach((token) => {
      cleanName = cleanName.replace(token, "");
    });
    tokens.task_name = cleanName.trim();

    setParsedTokens(tokens);
  };

  // Handle autocomplete selection
  const handleSelect = (value: string, option: AutocompleteOption) => {
    if (!activeToken) return;

    const { start } = activeToken;
    const prefix = activeToken.type === "project" ? "#" : "@";

    // Replace the token in input with selected value
    const before = inputText.substring(0, start);
    const after = inputText.substring(start).replace(/^[#@][^\s#@!^]*/, "");

    const newValue = `${before}${prefix}${value} ${after}`.trim();
    setInputText(newValue);
    setActiveToken(null);
    setAutocompleteOptions([]);

    // Update parsed tokens with the ID
    if (parsedTokens) {
      if (option.type === "project") {
        setParsedTokens({
          ...parsedTokens,
          project_name: value,
          project_id: option.id,
        });
      } else {
        setParsedTokens({
          ...parsedTokens,
          assignee_name: value,
          assignee_id: option.id,
        });
      }
    }
  };

  const handleSubmit = async () => {
    if (!inputText.trim()) return;

    // Check if we have a project (either from token or default)
    const projectId = parsedTokens?.project_id || defaultProjectId;
    const projectName = parsedTokens?.project_name;

    if (!projectId && !projectName) {
      message.warning("Specifica un progetto con #NomeProgetto");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/direct/smart-create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: inputText,
          default_module: "ProjectTask",
          default_project_id: projectId || defaultProjectId,
          project_id: parsedTokens?.project_id,
          assignee_id: parsedTokens?.assignee_id,
          auto_create_related: true,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setInputText("");
        setParsedTokens(null);
        setActiveToken(null);

        // Build success message
        const createdItems = data.created || [];
        const createdProject = createdItems.find(
          (e: { type: string }) => e.type === "Project"
        );

        let successMsg = "Task creato";
        if (createdProject) {
          successMsg += ` + nuovo progetto "${createdProject.name}"`;
        }

        message.success(successMsg);
        onTaskCreated?.();
      } else {
        message.error(data.error || "Errore nella creazione");
      }
    } catch (error) {
      console.error("Smart create error:", error);
      message.error("Errore di connessione");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && autocompleteOptions.length === 0) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Render parsed tokens as tags
  const renderTokenTags = () => {
    if (!parsedTokens || Object.keys(parsedTokens.raw_tokens).length === 0) {
      return null;
    }

    const tags = [];

    if (parsedTokens.project_name) {
      tags.push(
        <Tag key="project" color={TOKEN_COLORS.project}>
          #{parsedTokens.project_name}
        </Tag>
      );
    }
    if (parsedTokens.assignee_name) {
      tags.push(
        <Tag key="user" color={TOKEN_COLORS.user}>
          @{parsedTokens.assignee_name}
        </Tag>
      );
    }
    if (parsedTokens.priority) {
      tags.push(
        <Tag key="priority" color={TOKEN_COLORS.priority}>
          !{parsedTokens.priority}
        </Tag>
      );
    }
    if (parsedTokens.due_date) {
      tags.push(
        <Tag key="date" color={TOKEN_COLORS.date}>
          ^{parsedTokens.due_date}
        </Tag>
      );
    }

    return tags.length > 0 ? (
      <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
        {tags}
        {parsedTokens.task_name && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            → "{parsedTokens.task_name}"
          </Text>
        )}
      </div>
    ) : null;
  };

  return (
    <div>
      <Tooltip
        title={
          <div style={{ fontSize: 11 }}>
            <div>
              <strong>Sintassi rapida:</strong>
            </div>
            <div>#Progetto - collega progetto (autocomplete)</div>
            <div>@utente - assegna a (autocomplete)</div>
            <div>!now !tomorrow !week !next !someday</div>
            <div>^tomorrow ^friday ^+3d</div>
          </div>
        }
        placement="topLeft"
      >
        <AutoComplete
          style={{ width: "100%" }}
          options={autocompleteOptions}
          onSelect={(value, option) =>
            handleSelect(value, option as AutocompleteOption)
          }
          onSearch={handleInputChange}
          value={inputText}
          open={autocompleteOptions.length > 0}
        >
          <Input
            ref={inputRef as any}
            placeholder="Task... #Progetto @utente !now ^tomorrow"
            prefix={
              isLoading ? (
                <LoadingOutlined style={{ color: "#3d4f5f" }} />
              ) : (
                <PlusOutlined style={{ color: "#3d4f5f" }} />
              )
            }
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
        </AutoComplete>
      </Tooltip>
      {renderTokenTags()}
    </div>
  );
};
